import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Crosshair, Download, FileSpreadsheet, FileText, Mail, Paperclip, Search, X } from 'lucide-react';
import {
  ingestionService,
  type SourceAnchor,
  type SourceFileRef,
  type SourcePreview,
} from '@/services/ingestion';
import { formatDeskDateTime } from '@/utils/dates';
import { HighlightedText } from './SourceHighlight';
import { emailFindHint, initialEmailBodyMode, type EmailBodyMode } from './emailPreview';
import { cellMatches, stepMatch } from './sourceSearch';

/**
 * Source viewer — opens the ORIGINAL stored email / attachments of an intake
 * record in a popup so extracted datapoints can be verified against the
 * source without leaving the platform. Read-only; served sha-addressed from
 * the record's own file list. With an `anchor` (WP-J click-to-source), the
 * viewer scrolls to and highlights the exact place a value was read from.
 */

/** What a reviewer pointed at, handed back to the engine to read. */
export interface DesignatedPlace {
  sha256: string;
  filename: string;
  sheet?: string;
  cell?: string;
  lineNo?: number;
  text?: string;
}

export default function SourceViewerDialog({
  ingestionId,
  files,
  initialSha,
  anchor,
  designateFor,
  onDesignate,
  onClose,
  unavailableReason,
}: {
  ingestionId: string;
  files: SourceFileRef[];
  initialSha?: string;
  anchor?: SourceAnchor | null;
  /** Label of the datapoint being designated, when the viewer is opened to
   *  point the engine at a value rather than to verify one. */
  designateFor?: string | null;
  /** Designate mode. The viewer stays read-only: it reports a coordinate, and
   *  the engine reads the value there. */
  onDesignate?: (place: DesignatedPlace) => void;
  onClose: () => void;
  /** When set, skip the file API and show this message in the popup shell
   *  (e.g. demo fixtures or records with metadata but no stored binary). */
  unavailableReason?: string | null;
}) {
  const [activeSha, setActiveSha] = useState(initialSha ?? files[0]?.sha256);
  const [preview, setPreview] = useState<SourcePreview | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [sheetIdx, setSheetIdx] = useState(0);
  const [pdfMode, setPdfMode] = useState<'text' | 'original'>('original');
  const [emailMode, setEmailMode] = useState<EmailBodyMode>('formatted');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  // Find in this document. A 90-page OM and a 14-sheet model were openable and
  // unsearchable, so "is the guarantor named anywhere" meant reading.
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeMatch, setActiveMatch] = useState(0);
  const [textMatches, setTextMatches] = useState(0);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const activeCellRef = useRef<HTMLTableCellElement | null>(null);

  const active = files.find((f) => f.sha256 === activeSha) ?? files[0];
  // The anchor applies to the file it was opened for — not other tabs.
  const activeAnchor = activeSha === initialSha ? anchor : null;
  const unavailable = Boolean(unavailableReason);

  // Which cells of the workbook's visible sheet the query hits, keyed so a
  // cell can name its own place in the match order.
  const sheetHits = useMemo(() => {
    const hits = new Map<string, number>();
    if (preview?.kind !== 'xlsx' || !query.trim()) return hits;
    const rows = preview.sheets?.[sheetIdx]?.rows ?? [];
    let index = 0;
    rows.forEach((row, ri) => {
      row.forEach((cell, ci) => {
        if (cellMatches(cell, query)) {
          hits.set(`${ri}:${ci}`, index);
          index += 1;
        }
      });
    });
    return hits;
  }, [preview, sheetIdx, query]);

  // A find is only honest where there is text in this DOM to look through: the
  // embedded PDF renders inside an <object> and an image has no text at all.
  const searchable =
    (preview?.kind === 'email' && (!preview.html || emailMode === 'text')) ||
    preview?.kind === 'text' ||
    preview?.kind === 'xlsx' ||
    (preview?.kind === 'pdf' && pdfMode === 'text');

  const matchCount = preview?.kind === 'xlsx' ? sheetHits.size : textMatches;

  // Follow the active workbook hit, the way the text view follows its own.
  useEffect(() => {
    if (preview?.kind === 'xlsx') {
      activeCellRef.current?.scrollIntoView?.({ block: 'center', inline: 'center' });
    }
  }, [activeMatch, sheetHits, preview?.kind]);

  // A new file, a new sheet or a new PDF mode is a new document to search.
  useEffect(() => {
    setActiveMatch(0);
    setTextMatches(0);
  }, [activeSha, sheetIdx, pdfMode, emailMode]);

  const releaseObjectUrl = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  const load = useCallback(async (sha: string) => {
    setLoading(true);
    setError(null);
    setPreview(null);
    setPdfUrl(null);
    setSheetIdx(0);
    releaseObjectUrl();
    try {
      const pv = await ingestionService.getSourcePreview(ingestionId, sha);
      setPreview(pv);
      if (pv.kind === 'pdf') {
        // an anchored open lands on the extracted-text tab (where the
        // highlight is exact); otherwise the native viewer
        setPdfMode(sha === initialSha && anchor && pv.text ? 'text' : 'original');
        const blob = await ingestionService.getSourceBlob(ingestionId, sha);
        const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        objectUrlRef.current = url;
        setPdfUrl(url);
      }
      if (pv.kind === 'image') {
        // Register C1. Streamed as bytes rather than inlined in the preview
        // JSON: a base64 payload would bloat every response and defeat HTTP
        // caching on the one file type most likely to be several megabytes.
        const blob = await ingestionService.getSourceBlob(ingestionId, sha);
        const url = URL.createObjectURL(
          new Blob([blob], { type: pv.contentType ?? 'image/jpeg' }),
        );
        objectUrlRef.current = url;
        setPdfUrl(url);
      }
      if (pv.kind === 'email') {
        const preferText = Boolean(
          (sha === initialSha && anchor?.kind?.startsWith('email')) || designateFor,
        );
        setEmailMode(initialEmailBodyMode(Boolean(pv.html), preferText));
      }
      if (pv.kind === 'xlsx' && sha === initialSha && anchor?.cell) {
        const sheetName = anchor.cell.split('!')[0];
        const idx = (pv.sheets ?? []).findIndex((s) => s.name === sheetName);
        if (idx >= 0) setSheetIdx(idx);
      }
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string }; status?: number } })?.response?.data?.detail;
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 409 || (typeof detail === 'string' && detail.includes('unavailable'))) {
        setError(
          typeof detail === 'string'
            ? `${detail} Drop the original .eml/files on Intake again — re-upload rehydrates the stored copies onto durable storage.`
            : 'Stored file unavailable. Re-upload the original on Intake to restore it.',
        );
      } else {
        setError(typeof detail === 'string' ? detail : 'Could not load this file.');
      }
    } finally {
      setLoading(false);
    }
  }, [ingestionId, initialSha, anchor, designateFor]);

  // "Sheet!B14" → sheet name + zero-based row/col for the grid highlight
  const anchorCell = useMemo(() => {
    const cell = activeSha === initialSha ? anchor?.cell : null;
    if (!cell) return null;
    const m = /^(?:(.+)!)?([A-Z]+)(\d+)$/.exec(cell);
    if (!m) return null;
    const col = m[2].split('').reduce((a, c) => a * 26 + (c.charCodeAt(0) - 64), 0) - 1;
    return { sheet: m[1] ?? null, row: parseInt(m[3], 10) - 1, col };
  }, [activeSha, initialSha, anchor]);

  useEffect(() => {
    if (preview?.kind === 'xlsx' && anchorCell) {
      document.getElementById('cmos-anchored-cell')?.scrollIntoView({ block: 'center', inline: 'center' });
    }
  }, [preview, sheetIdx, anchorCell]);

  useEffect(() => {
    if (unavailable) {
      setLoading(false);
      setError(null);
      setPreview(null);
      setPdfUrl(null);
      releaseObjectUrl();
      return;
    }
    if (activeSha) void load(activeSha);
    return releaseObjectUrl;
  }, [activeSha, load, unavailable]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const download = async () => {
    if (!active || unavailable) return;
    try {
      const blob = await ingestionService.getSourceBlob(ingestionId, active.sha256);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = active.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Download failed. Please retry.');
    }
  };

  const designating = Boolean(onDesignate);

  /** Excel's A1 name for a zero-based column index. */
  const columnName = (index: number): string => {
    let n = index + 1;
    let name = '';
    while (n > 0) {
      const rem = (n - 1) % 26;
      name = String.fromCharCode(65 + rem) + name;
      n = Math.floor((n - 1) / 26);
    }
    return name;
  };

  const designateCell = (row: number, col: number) => {
    if (!onDesignate || !active) return;
    const sheet = preview?.sheets?.[sheetIdx]?.name;
    onDesignate({
      sha256: active.sha256,
      filename: active.filename,
      sheet,
      cell: `${columnName(col)}${row + 1}`,
    });
  };

  const designateLine = (lineNo: number, text: string) => {
    if (!onDesignate || !active) return;
    onDesignate({
      sha256: active.sha256, filename: active.filename, lineNo, text,
    });
  };

  const fileIcon = (f: SourceFileRef) =>
    f.kind === 'email_source' ? <Mail size={11} className="text-blue-400" />
    : /\.(xlsx|xlsm|csv)$/i.test(f.filename) ? <FileSpreadsheet size={11} className="text-emerald-400" />
    : <FileText size={11} className="text-stone-400" />;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Source document"
        className="relative flex flex-col rounded-xl border border-amber-400/15 bg-[#090909] shadow-2xl"
        style={{ width: '90%', height: '88%', maxWidth: 1200 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* File tabs + actions */}
        <div className="flex items-center gap-1 border-b border-amber-400/10 px-3 flex-shrink-0" style={{ height: 44 }}>
          <Paperclip size={12} className="text-stone-600 mr-1" />
          <div className="flex items-center gap-1 overflow-x-auto">
            {files.map((f) => (
              <button
                key={f.sha256}
                onClick={() => setActiveSha(f.sha256)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded-t whitespace-nowrap transition-colors relative ${
                  f.sha256 === active?.sha256 ? 'text-amber-300' : 'text-stone-500 hover:text-stone-300'
                }`}
                title={f.filename}
              >
                {fileIcon(f)}
                <span className="max-w-[220px] truncate">
                  {f.kind === 'email_source' ? 'Original email' : f.filename}
                </span>
                {f.sha256 === active?.sha256 && (
                  <span className="absolute bottom-0 left-2 right-2 h-[2px] bg-amber-400 rounded-t" />
                )}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-1">
            {!unavailable && (
              <button
                onClick={() => {
                  const next = !searchOpen;
                  setSearchOpen(next);
                  if (!next) setQuery('');
                  if (next) window.setTimeout(() => searchInputRef.current?.focus(), 0);
                }}
                title="Find in this file"
                aria-label="Find in this file"
                aria-pressed={searchOpen}
                className={`rounded-md p-1.5 transition-colors hover:bg-white/5 ${
                  searchOpen ? 'text-amber-300' : 'text-stone-500 hover:text-amber-300'
                }`}
              >
                <Search size={14} />
              </button>
            )}
            {!unavailable && (
              <button
                onClick={download}
                title="Download original file"
                className="rounded-md p-1.5 text-stone-500 hover:text-amber-300 hover:bg-white/5 transition-colors"
              >
                <Download size={14} />
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded-md p-1.5 text-stone-500 hover:text-stone-200 hover:bg-white/5 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {searchOpen && !unavailable && (
          <div className="flex items-center gap-2 border-b border-amber-400/10 bg-black/30 px-3 py-1.5">
            <Search size={12} className="shrink-0 text-stone-600" />
            <input
              ref={searchInputRef}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveMatch(0);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  setActiveMatch((current) =>
                    stepMatch(current, matchCount, event.shiftKey ? -1 : 1));
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  setSearchOpen(false);
                  setQuery('');
                }
              }}
              placeholder={searchable ? `Find in ${active?.filename ?? 'this file'}` : 'Find'}
              aria-label="Find in this file"
              className="min-w-0 flex-1 bg-transparent text-[11px] text-stone-200 placeholder:text-stone-600 focus:outline-none"
            />
            {searchable ? (
              <>
                <span className="shrink-0 font-mono text-[10px] text-stone-500">
                  {query.trim() ? `${matchCount ? activeMatch + 1 : 0} / ${matchCount}` : ''}
                </span>
                <button
                  type="button"
                  onClick={() => setActiveMatch((c) => stepMatch(c, matchCount, -1))}
                  disabled={matchCount === 0}
                  title="Previous match"
                  aria-label="Previous match"
                  className="rounded p-1 text-stone-500 hover:text-amber-300 hover:bg-white/5 disabled:opacity-30"
                >
                  <ChevronUp size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => setActiveMatch((c) => stepMatch(c, matchCount, 1))}
                  disabled={matchCount === 0}
                  title="Next match"
                  aria-label="Next match"
                  className="rounded p-1 text-stone-500 hover:text-amber-300 hover:bg-white/5 disabled:opacity-30"
                >
                  <ChevronDown size={12} />
                </button>
              </>
            ) : (
              // Said plainly rather than searching nothing: the embedded PDF
              // and an image have no text in this DOM to look through.
              <span className="shrink-0 text-[10px] text-stone-500">
                {emailFindHint(preview?.kind, emailMode)}
              </span>
            )}
            <button
              type="button"
              onClick={() => { setSearchOpen(false); setQuery(''); }}
              title="Close find"
              aria-label="Close find"
              className="rounded p-1 text-stone-500 hover:text-stone-200 hover:bg-white/5"
            >
              <X size={12} />
            </button>
          </div>
        )}

        {designating && (
          <div className="flex items-center gap-2 border-b border-amber-400/20 bg-amber-400/[0.07] px-4 py-2">
            <Crosshair size={12} className="shrink-0 text-amber-400" />
            <span className="text-[11px] text-amber-200">
              Click the cell or line that holds{' '}
              <span className="font-medium">{designateFor ?? 'this datapoint'}</span> —
              the engine will read it there and bring the value back for you to confirm.
            </span>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {unavailable && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 px-8 py-10 text-center">
              <FileText size={28} className="text-stone-600" />
              <p className="text-[13px] text-stone-300">{active?.filename ?? 'Attachment'}</p>
              <p className="max-w-md text-[12px] text-stone-500">{unavailableReason}</p>
            </div>
          )}
          {!unavailable && loading && (
            <p className="text-[12px] text-stone-500 py-10 text-center">Loading source…</p>
          )}
          {!unavailable && error && !loading && (
            <p className="text-[12px] text-red-300 py-10 text-center">{error}</p>
          )}

          {!unavailable && !loading && !error && preview?.kind === 'email' && (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="shrink-0 space-y-1 rounded-lg border border-stone-800 bg-black/30 px-3 py-2.5 mx-5 mt-4 mb-2 text-[11px]">
                <div className="flex gap-2"><span className="text-stone-500 w-[64px] shrink-0">From</span>
                  <span className="text-stone-200">{preview.envelope?.fromName ?? ''} {preview.envelope?.fromEmail ? `<${preview.envelope.fromEmail}>` : ''}</span></div>
                <div className="flex gap-2"><span className="text-stone-500 w-[64px] shrink-0">To</span>
                  <span className="text-stone-300">{(preview.envelope?.to ?? []).join(', ') || '—'}</span></div>
                <div className="flex gap-2"><span className="text-stone-500 w-[64px] shrink-0">Subject</span>
                  <span className="text-stone-200">{preview.envelope?.subject ?? '—'}</span></div>
                <div className="flex gap-2"><span className="text-stone-500 w-[64px] shrink-0">Sent</span>
                  <span className="text-stone-300">{formatDeskDateTime(preview.envelope?.sentAt) || '—'}</span></div>
                {(preview.attachments?.length ?? 0) > 0 && (
                  <div className="flex gap-2"><span className="text-stone-500 w-[64px] shrink-0">Attached</span>
                    <span className="text-stone-300">{preview.attachments!.join(', ')}</span></div>
                )}
              </div>
              {preview.html ? (
                <div className="flex shrink-0 items-center gap-1 px-5 pt-1">
                  {(['formatted', 'text'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setEmailMode(mode)}
                      className={`rounded px-2 py-1 text-[10px] ${
                        emailMode === mode ? 'bg-amber-400/15 text-amber-300' : 'text-stone-500 hover:text-stone-300'
                      }`}
                    >
                      {mode === 'formatted' ? 'Formatted' : 'Extracted text'}
                    </button>
                  ))}
                </div>
              ) : null}
              {emailMode === 'formatted' && preview.html ? (
                <div className="mx-5 mb-4 mt-2 min-h-0 flex-1 overflow-hidden rounded-lg border border-stone-800 bg-white">
                  <iframe
                    title="Original email"
                    srcDoc={preview.html}
                    sandbox=""
                    referrerPolicy="no-referrer"
                    className="h-full w-full border-0 bg-white"
                  />
                </div>
              ) : preview.text ? (
                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
                  <HighlightedText
                    text={preview.text}
                    anchor={activeAnchor?.kind?.startsWith('email') ? activeAnchor : null}
                    onDesignateLine={onDesignate ? designateLine : undefined}
                    query={searchOpen ? query : undefined}
                    activeMatch={activeMatch}
                    onMatchCount={setTextMatches}
                  />
                  {preview.truncated && (
                    <p className="mt-2 text-[10px] text-amber-300">Body truncated for preview — download for the full file.</p>
                  )}
                </div>
              ) : (
                <p className="px-5 py-6 text-[12.5px] text-stone-400">(no readable body)</p>
              )}
            </div>
          )}

          {!unavailable && !loading && !error && preview?.kind === 'xlsx' && (
            <>
              {(preview.sheets?.length ?? 0) > 1 && (
                <div className="flex items-center gap-1 px-4 pt-2 flex-shrink-0">
                  {preview.sheets!.map((s, i) => (
                    <button
                      key={s.name}
                      onClick={() => setSheetIdx(i)}
                      className={`px-2 py-1 text-[10px] rounded ${i === sheetIdx ? 'bg-amber-400/15 text-amber-300' : 'text-stone-500 hover:text-stone-300'}`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex-1 overflow-auto px-4 py-3">
                <table className="border-collapse text-[11px] font-mono">
                  <tbody>
                    {(preview.sheets?.[sheetIdx]?.rows ?? []).map((row, ri) => (
                      <tr key={ri}>
                        <td className="border border-stone-800 bg-black/40 px-1.5 py-0.5 text-stone-600 text-right select-none">{ri + 1}</td>
                        {row.map((cell, ci) => {
                          const anchored =
                            anchorCell !== null
                            && ri === anchorCell.row && ci === anchorCell.col
                            && (anchorCell.sheet === null
                                || preview.sheets?.[sheetIdx]?.name === anchorCell.sheet);
                          const hitIndex = sheetHits.get(`${ri}:${ci}`);
                          const isHit = hitIndex !== undefined;
                          const isActiveHit = hitIndex === activeMatch;
                          return (
                            <td
                              key={ci}
                              ref={isActiveHit ? activeCellRef : undefined}
                              data-testid={isHit ? 'source-search-cell' : undefined}
                              onClick={designating ? () => designateCell(ri, ci) : undefined}
                              title={designating ? `Use ${columnName(ci)}${ri + 1}` : undefined}
                              id={anchored ? 'cmos-anchored-cell' : undefined}
                              className={`border px-2 py-0.5 whitespace-nowrap max-w-[280px] overflow-hidden text-ellipsis ${
                                anchored
                                  ? 'border-amber-400 bg-amber-400/20 text-amber-100 ring-1 ring-amber-400'
                                  : isActiveHit
                                    ? 'border-amber-300 bg-amber-300 text-black'
                                    : isHit
                                      ? 'border-stone-800 bg-amber-400/25 text-stone-100'
                                      : 'border-stone-800 text-stone-200'
                              } ${designating ? 'cursor-crosshair hover:bg-amber-400/15 hover:text-amber-100' : ''}`}
                            >
                              {typeof cell === 'number' ? cell.toLocaleString() : cell ?? ''}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.truncated && (
                  <p className="mt-2 text-[10px] text-amber-300">Preview truncated — download for the full workbook.</p>
                )}
              </div>
            </>
          )}

          {!unavailable && !loading && !error && preview?.kind === 'pdf' && (
            <div className="flex-1 flex flex-col overflow-hidden">
              {preview.text != null && (
                <div className="flex items-center gap-1 px-4 pt-2 flex-shrink-0">
                  {(['original', 'text'] as const).map((mode) => (
                    <button
                      key={mode}
                      onClick={() => setPdfMode(mode)}
                      className={`px-2 py-1 text-[10px] rounded ${
                        pdfMode === mode ? 'bg-amber-400/15 text-amber-300' : 'text-stone-500 hover:text-stone-300'
                      }`}
                    >
                      {mode === 'original' ? 'Original PDF' : 'Extracted text'}
                    </button>
                  ))}
                  {pdfMode === 'text' && activeAnchor?.page && (
                    <span className="ml-2 text-[10px] text-stone-500">
                      Value read on page {activeAnchor.page}
                    </span>
                  )}
                </div>
              )}
              {pdfMode === 'original' && pdfUrl && (
                <object data={pdfUrl} type="application/pdf" className="flex-1 w-full">
                  <p className="text-[12px] text-stone-400 py-10 text-center">
                    Your browser cannot display PDFs inline — use the download button above.
                  </p>
                </object>
              )}
              {pdfMode === 'text' && preview.text != null && (
                <div className="flex-1 overflow-y-auto px-5 py-4">
                  <HighlightedText
                    text={preview.text}
                    anchor={activeAnchor?.kind === 'pdf' ? activeAnchor : null}
                    pageStartLines={preview.pageStartLines}
                    mono
                    onDesignateLine={onDesignate ? designateLine : undefined}
                    query={searchOpen ? query : undefined}
                    activeMatch={activeMatch}
                    onMatchCount={setTextMatches}
                  />
                  {preview.truncated && (
                    <p className="mt-2 text-[10px] text-amber-300">Text truncated for preview — use the original tab for the full document.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {!unavailable && !loading && !error && preview?.kind === 'text' && (
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {preview.text ? (
                <HighlightedText text={preview.text} anchor={activeAnchor} mono
                  onDesignateLine={onDesignate ? designateLine : undefined}
                  query={searchOpen ? query : undefined}
                  activeMatch={activeMatch}
                  onMatchCount={setTextMatches} />
              ) : (
                <p className="text-[12px] text-stone-400">(empty)</p>
              )}
              {preview.truncated && (
                <p className="mt-2 text-[10px] text-amber-300">Truncated for preview — download for the full file.</p>
              )}
            </div>
          )}

          {!unavailable && !loading && !error && preview?.kind === 'image' && (
            <div className="flex flex-1 items-center justify-center overflow-auto p-4">
              {pdfUrl ? (
                <img
                  src={pdfUrl}
                  alt={preview.filename}
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <p className="text-[12px] text-stone-400">Loading image…</p>
              )}
            </div>
          )}

          {!unavailable && !loading && !error && preview?.kind === 'binary' && (
            <p className="text-[12px] text-stone-400 py-10 text-center">
              {preview.detail ?? 'No in-platform preview for this file type.'} Use the download button above.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
